import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';

import CustomButton from 'components/CustomButton';
import { AutoCompleteSelect, SimpleSelect } from 'components/CustomSelectFields';
import { useServices } from 'contexts';
import { getCaseMasterTags } from 'services';
import { CaseFacets, CaseAttemptFacet, toTagLabel } from './constant';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(5),
    marginTop: theme.spacing(6),
  },
  // Mirrors a select's footprint: label, its 8px gap, then the 44px field, at
  // the 200px minimum width the selects use.
  facetSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '200px',
  },
  skeleton: {
    borderRadius: '4px',
  },
}));

// Sentinel for the "All …" entry that clears a facet. It carries a truthy _id so
// SimpleSelect renders its name once picked rather than falling back to the
// placeholder, and the id is stripped before filters reach the API.
const ALL_OPTION_ID = '__all__';

// One request feeds five dropdowns, so it cannot ride on the API's default
// page size: whichever facets fall past the first page come back empty and
// their dropdown silently offers nothing but "All ...".
const TAG_PAGE_SIZE = 500;

// `jobRole` is the only facet whose key is more than one word, so it is the
// only one whose stored type might be spelled differently (job_role, JobRole).
// Matching on letters and digits alone keeps such a tag in its own dropdown.
const normalizeType = (type) => String(type || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Tag-backed facets first, then the attempt status, matching the filter row.
// Only a facet without its own `options` needs the tag lookup.
const AllFacets = [...CaseFacets, CaseAttemptFacet];

const CaseFilters = ({ onChange = () => { } }) => {
  const classes = useStyles();
  const service = useServices();

  const [loading, setLoading] = useState(true);
  const [tagsByType, setTagsByType] = useState({});
  const [selected, setSelected] = useState({});

  // One request covers every dropdown: `type` filters with `in`, so all five
  // facets come back together and are grouped by facet here.
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const result = await service.callService(getCaseMasterTags, {
          type: CaseFacets.map(({ key }) => key),
          pageSize: TAG_PAGE_SIZE,
          includeTotal: true,
        });

        const casemasterTags = result?.casemasterTags || [];
        const unmatchedTypes = new Set();

        const grouped = casemasterTags.reduce((acc, tag) => {
          const facet = CaseFacets.find(
            ({ key }) => normalizeType(key) === normalizeType(tag?.type)
          );

          if (!facet) {
            unmatchedTypes.add(tag?.type);
            return acc;
          }

          if (!acc[facet.key]) acc[facet.key] = [];
          acc[facet.key].push(tag);
          return acc;
        }, {});

        // An empty dropdown is indistinguishable from a facet nobody has
        // tagged yet, so say which case it is rather than leaving it blank.
        if (result?.total > casemasterTags.length) {
          console.warn(
            `Case filters: ${result.total} tags exist but ${casemasterTags.length} were fetched - raise TAG_PAGE_SIZE.`
          );
        }
        if (unmatchedTypes.size) {
          console.warn('Case filters: tags returned under an unknown type:', [...unmatchedTypes]);
        }

        const emptyFacets = CaseFacets
          .filter(({ key }) => !grouped[key]?.length)
          .map(({ key }) => key);

        if (emptyFacets.length) {
          console.warn('Case filters: the API returned no tags for:', emptyFacets);
        }

        setTagsByType(grouped);
      } catch (error) {
        console.error('Error fetching case filters:', error);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(() => (
    AllFacets.reduce((acc, { key, allLabel, options: fixedOptions }) => {
      // Tag names are stored lowercase, and the cards already title-case them
      // on the way out, so the dropdowns have to read the same way. The fixed
      // facet writes its own labels and needs no conversion.
      const facetOptions = fixedOptions || (tagsByType[key] || []).map(
        (tag) => ({ ...tag, name: toTagLabel(tag.name) })
      );

      acc[key] = [
        { _id: ALL_OPTION_ID, name: allLabel },
        ...facetOptions,
      ];
      return acc;
    }, {})
  ), [tagsByType]);

  // Only real selections travel outward; the "All …" sentinel means no filter.
  const applySelection = useCallback((next) => {
    setSelected(next);

    onChange(AllFacets.reduce((acc, facet) => {
      const chosen = next[facet.key];

      if (chosen?._id && chosen._id !== ALL_OPTION_ID) acc[facet.key] = chosen._id;

      return acc;
    }, {}));
  }, [onChange]);

  const handleSelect = useCallback((key) => (option) => {
    applySelection({ ...selected, [key]: option });
  }, [selected, applySelection]);

  const handleClear = useCallback((key) => () => {
    const next = { ...selected };

    delete next[key];

    applySelection(next);
  }, [selected, applySelection]);

  // A facet only offers a cross once it holds a real tag, not the "All …" reset.
  const isFiltered = useCallback(
    (key) => Boolean(selected[key]?._id) && selected[key]._id !== ALL_OPTION_ID,
    [selected]
  );

  const handleClearAll = useCallback(() => applySelection({}), [applySelection]);

  const hasFilters = AllFacets.some(({ key }) => isFiltered(key));

  return (
    <Box className={classes.root}>
      {AllFacets.map(({ key, label, allLabel, options: fixedOptions }) => {
        // A tag facet has nothing to show until its options arrive. The status
        // facet carries its own options, so it stays usable throughout.
        if (!fixedOptions && loading) {
          return (
            <Box key={key} className={classes.facetSkeleton}>
              <Skeleton variant='text' width={80} height={20} className={classes.skeleton} />
              <Skeleton
                variant='rectangular' height={44}
                className={classes.skeleton}
              />
            </Box>
          );
        }

        const shared = {
          name: key,
          label,
          options: options[key],
          placeholder: allLabel,
          value: selected[key],
          onSelect: handleSelect(key),
          onClear: isFiltered(key) ? handleClear(key) : undefined,
        };

        // The tag facets grow as tags are added, so they get the searchable,
        // scrolling menu. The attempt status is two fixed options that need
        // neither a search box nor a fetch to wait on.
        return fixedOptions
          ? <SimpleSelect key={key} {...shared} />
          : <AutoCompleteSelect key={key} {...shared} />;
      })}

      {hasFilters && (
        <CustomButton
          variant='outlined'
          onClick={handleClearAll}
          // The selects carry a label above them, so bottom-aligning keeps this
          // level with the fields rather than with the labels. Passed as sx, not
          // className - CustomButton spreads props after its own className, so a
          // className prop would replace the button's base styles outright.
          sx={{ alignSelf: 'flex-end' }}
        >
          Clear filters
        </CustomButton>
      )}
    </Box>
  );
};

export default CaseFilters;
